import "@/pages/landing-v2.css";
import "@/pages/creative-tools.css";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Check, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import {
  trackBeginCheckout,
  trackUpgradeModalShown,
  trackUpgradeModalDismissed,
} from "@/lib/dataLayer";
import { getStoredGclid } from "@/lib/attribution";
import { getReferralId } from "@/lib/referral";
import { savePendingCheckout } from "@/lib/pendingCheckout";
import {
  originatingProjectId,
  saveCheckoutReturn,
} from "@/lib/checkoutReturn";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import AuthModal from "@/components/AuthModal";
import { SilentErrorBoundary } from "@/components/SilentErrorBoundary";
import {
  PRICING_PLANS,
  CREDIT_PACK_ORDER,
  BEST_VALUE_PACK_ID,
  getBasePlan,
  type CreditPackId,
  type PricingPlan,
} from "@/lib/pricingPlans";
import { toast } from "sonner";
import { useLocation } from "wouter";
import PricingFAQSection from "@/components/PricingFAQSection";

/** Which content tab the modal opens on. */
export type UpgradeModalTab = "monthly" | "yearly" | "topup";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  description?: string;
  trigger?: string;
  /**
   * Initial tab to render. Use "topup" when the modal is opened from a
   * "Top up credits" chat chip or button, so users land directly on the
   * pack grid instead of having to click an extra tab.
   */
  defaultTab?: UpgradeModalTab;
}

/** Cards rendered in the modal — Free is shown first as a comparison anchor. */
const MODAL_CARDS: PricingPlan[] = PRICING_PLANS;

export default function UpgradeModal(props: UpgradeModalProps) {
  if (!props.open) return null;
  return (
    <SilentErrorBoundary
      resetKeys={[props.open, props.description, props.trigger]}
    >
      <UpgradeModalInner {...props} />
    </SilentErrorBoundary>
  );
}

function UpgradeModalInner({
  open,
  onClose,
  description,
  trigger = "unknown",
  defaultTab = "yearly",
}: UpgradeModalProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  // Tab state combines billing period and the top-up surface. When tab is
  // "topup" the modal swaps from the subscription card list to the top-up
  // pack grid; when monthly/yearly it behaves like a normal upgrade picker.
  const [theme, setTheme] = useState("dark");
  useEffect(() => {
    if (open) {
      try { setTheme(document.querySelector(".creative-landing[data-theme]")?.getAttribute("data-theme") || localStorage.getItem("solmi-theme") || "dark"); } catch { setTheme("dark"); }
    }
  }, [open]);
  const [tab, setTab] = useState<UpgradeModalTab>(defaultTab);
  const billingPeriod: "monthly" | "yearly" =
    tab === "yearly" ? "yearly" : "monthly";
  // Tracks which card's "Get [Plan]" was clicked, so only that card's
  // button shows the loading spinner during the brief redirect to Stripe.
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { data: subscription } = trpc.payment.getSubscription.useQuery(
    undefined,
    { enabled: isAuthenticated },
  );

  const currentBasePlan = getBasePlan(subscription?.plan || "free");
  const hasActivePaidSub =
    !!subscription &&
    subscription.plan !== "free" &&
    subscription.status === "active";

  // Top-up pack data + checkout mutation.
  const { data: creditPacks } = trpc.payment.getCreditPacks.useQuery(
    undefined,
    { enabled: open && tab === "topup" },
  );
  const createCreditPackMutation =
    trpc.payment.createCreditPackCheckout.useMutation({
      onSuccess: (data, variables) => {
        saveCheckoutReturn(data, variables);
        if (data.url) {
          window.location.href = data.url;
        }
      },
      onError: (error) => {
        toast.error(error.message || t("pricing.checkoutFailed"));
      },
    });

  const handleBuyPack = (packId: CreditPackId) => {
    posthog.capture("topup_pack_clicked", {
      source: "upgrade_modal",
      trigger,
      packId,
    });
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    // Packs are open to everyone (no plan required since Jul 2026) — free
    // users proceed straight to Stripe checkout like subscribers.
    // Fire begin_checkout for the credit-pack so the GA4 ecommerce funnel
    // sees credit top-ups, not just plan subscriptions. PostHog gets the
    // plan-aware `checkout_started` event from the same helper.
    const pack = creditPacks?.[packId];
    if (pack) {
      trackBeginCheckout(
        [
          {
            item_id: `credits_${packId}`,
            item_name: `Credits ${packId} (${pack.credits.toLocaleString()})`,
            price: pack.price,
            quantity: 1,
            item_category: "credits",
          },
        ],
        pack.price,
        "USD",
        { trigger, source: "upgrade_modal_topup" },
      );
    }
    createCreditPackMutation.mutate({
      packId,
      gclid: getStoredGclid() ?? undefined,
      clientReferenceId: getReferralId(),
      // Buyer is usually mid-project when the credit gate opens this modal —
      // carry that project through so the success page leads back into it.
      returnProjectId: originatingProjectId(),
    });
  };

  // Reset the tab to whatever the caller asked for each time the modal opens.
  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setPendingPlanId(null);
    }
  }, [open, defaultTab]);

  const utils = trpc.useUtils();
  const createCheckoutMutation =
    trpc.payment.createSubscriptionCheckout.useMutation({
      onSuccess: (data, variables) => {
        if (data.switched) {
          // Existing subscribers are switched in place on Stripe (prorated) —
          // there is no checkout page to redirect to.
          utils.payment.getSubscription.invalidate();
          toast.success(
            t("subscription.planSwitched", "Your plan has been updated."),
          );
          onClose();
          return;
        }
        saveCheckoutReturn(data, variables);
        if (data.url) {
          window.location.href = data.url;
        }
      },
      onError: (error) => {
        toast.error(error.message || t("pricing.checkoutFailed"));
      },
    });

  useEffect(() => {
    if (open) {
      // Dual-fire to GA4 + PostHog via the shared helper. We pass the
      // current plan and which tab the modal opened on so we can slice
      // by "out of credits → opened on top-up tab" vs. "free → monthly".
      trackUpgradeModalShown({
        trigger,
        defaultTab,
        currentPlan: subscription?.plan || "free",
      });
    }
  }, [open, trigger, defaultTab, subscription?.plan]);

  const handleClose = () => {
    trackUpgradeModalDismissed({
      trigger,
      defaultTab,
      currentPlan: subscription?.plan || "free",
    });
    onClose();
  };

  // Each plan card has its own "Get [Plan]" CTA — no global selectedPlan.
  // This handler is called with the specific planId the user clicked on.
  const handleSubscribe = (planId: string) => {
    if (planId === "free") return; // Free card is informational only.
    const plan = MODAL_CARDS.find((p) => p.id === planId);
    if (!plan) return;
    const fullPlanId = `${planId}_${billingPeriod}`;
    const totalPrice =
      billingPeriod === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;

    posthog.capture("upgrade_cta_clicked", {
      source: "modal",
      trigger,
      plan: fullPlanId,
    });

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    posthog.capture("checkout_started_from_modal", {
      trigger,
      plan: fullPlanId,
    });
    trackBeginCheckout(
      [
        {
          item_id: fullPlanId,
          item_name: plan.name,
          price: totalPrice,
          quantity: 1,
          item_category: "subscription",
          item_variant: billingPeriod,
        },
      ],
      totalPrice,
      "USD",
      { trigger, source: "upgrade_modal" },
    );
    setPendingPlanId(planId);
    savePendingCheckout({
      kind: "subscription",
      planId: fullPlanId,
      planName: `${plan.name} ${billingPeriod === "yearly" ? "Yearly" : "Monthly"}`,
      price: totalPrice,
    });
    createCheckoutMutation.mutate({
      planId: fullPlanId as any,
      gclid: getStoredGclid() ?? undefined,
      clientReferenceId: getReferralId(),
      returnProjectId: originatingProjectId(),
    });
  };

  const handleViewAllPlans = () => {
    posthog.capture("view_all_plans_clicked", {
      source: "upgrade_modal",
      trigger,
    });
    onClose();
    setLocation("/subscription");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          showCloseButton={false}
          data-theme={theme}
          className="slv2 creative-landing subscription-landing upgrade-landing sm:max-w-6xl bg-background border-border p-0 gap-0 max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 z-50 p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full bg-card text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label={t("pricing.closeAria")}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="px-4 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8">
            {/* Centered header */}
            <div className="text-center mb-6 sm:mb-8 max-w-xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                {t("pricing.chooseYourPlan")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {description || t("pricing.chooseYourPlanDefaultDescription")}
              </p>
            </div>

            {/* Centered tab group: Monthly | Yearly | Top-up credits.
                Top-up swaps the lower section to the pack grid. */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex flex-wrap justify-center items-center gap-1 bg-card rounded-full p-1 border border-border">
                <button
                  onClick={() => setTab("monthly")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    tab === "monthly"
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("pricing.monthly")}
                </button>
                <button
                  onClick={() => setTab("yearly")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    tab === "yearly"
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("pricing.yearly")}
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      tab === "yearly"
                        ? "bg-white/20 text-foreground"
                        : "bg-green-500/20 text-primary"
                    }`}
                  >
                    {t("pricing.yearlyDiscount")}
                  </span>
                </button>
                <button
                  onClick={() => setTab("topup")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                    tab === "topup"
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("pricing.topUpCreditsTab")}
                </button>
              </div>
            </div>

            {tab === "topup" ? (
              <div className="max-w-2xl mx-auto">
                <TopUpPacksPanel
                  packs={creditPacks}
                  onBuy={handleBuyPack}
                  isLoading={createCreditPackMutation.isPending}
                  hasActivePaidSub={hasActivePaidSub}
                  isAuthenticated={isAuthenticated}
                  onSwitchToPlans={() => setTab("monthly")}
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                  {MODAL_CARDS.map((plan) => {
                    const isCurrent = currentBasePlan === plan.id;
                    const isFree = plan.id === "free";
                    const isYearly = billingPeriod === "yearly";
                    const monthlyEquivalent = isYearly
                      ? plan.yearlyPrice / 12
                      : plan.monthlyPrice;
                    const isPending =
                      createCheckoutMutation.isPending &&
                      pendingPlanId === plan.id;
                    return (
                      <div
                        key={plan.id}
                        className={`relative flex flex-col rounded-2xl border-2 p-5 transition-all ${
                          plan.popular
                            ? "subscription-recommended bg-card"
                            : "border-border bg-card"
                        }`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="subscription-badge text-[11px] font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                              <Sparkles className="h-3 w-3" />
                              {t("pricing.mostPopular")}
                            </span>
                          </div>
                        )}

                        <h3 className="text-lg font-bold text-foreground mb-3">
                          {t(`pricing.${plan.id === "pro_v2" ? "pro" : plan.id}`, { defaultValue: plan.name })}
                        </h3>

                        <div className="flex items-baseline gap-1.5">
                          {isYearly && !isFree && (
                            <span className="text-base text-muted-foreground line-through">
                              ${plan.monthlyPrice}
                            </span>
                          )}
                          <span className="text-3xl font-bold text-foreground">
                            ${Math.round(monthlyEquivalent)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("pricing.perMonth")}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1 mb-4">
                          {isFree
                            ? t("pricing.free")
                            : isYearly
                              ? t("pricing.billedAnnually")
                              : t("pricing.billedMonthly")}
                        </p>

                        <Button
                          onClick={() => handleSubscribe(plan.id)}
                          disabled={
                            isFree ||
                            isCurrent ||
                            createCheckoutMutation.isPending
                          }
                          className={`w-full h-10 rounded-full text-sm font-semibold mb-4 ${
                            isFree || isCurrent
                              ? "bg-card cursor-not-allowed opacity-50 text-muted-foreground"
                              : plan.popular
                                ? "subscription-cta"
                                : "subscription-cta"
                          }`}
                        >
                          {isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                              {t("pricing.redirecting")}
                            </>
                          ) : isCurrent ? (
                            t("pricing.yourCurrentPlan")
                          ) : isFree ? (
                            t("pricing.free")
                          ) : (
                            t("pricing.getPlanCta", {
                              plan: t(
                                `pricing.${plan.id === "pro_v2" ? "pro" : plan.id}`,
                                { defaultValue: plan.name },
                              ),
                            })
                          )}
                        </Button>

                        {/* Highlighted credits box with concrete-output bullets.
                            Phrasing intentionally uses "polished 1-min final
                            video projects" rather than a flat "X videos" — a
                            video's credit cost varies with duration. */}
                        <div
                          className={`rounded-xl border p-3 mb-4 ${
                            plan.popular
                              ? "border-primary/30 bg-primary/10"
                              : "border-border bg-card"
                          }`}
                        >
                          <p className="text-xs font-semibold text-foreground flex items-center gap-1.5 mb-1.5">
                            <Sparkles
                              className={`h-3 w-3 ${
                                plan.popular
                                  ? "text-primary"
                                  : "text-primary"
                              }`}
                            />
                            {t(
                              plan.creditsHeadlineKey ??
                                (plan.creditsCadence === "daily"
                                  ? "pricing.creditsDailyHeadline"
                                  : "pricing.creditsMonthlyHeadline"),
                              {
                                count: plan.credits,
                                defaultValue: plan.creditsLabel,
                              },
                            )}
                          </p>
                          <ul className="space-y-0.5 text-[11px] text-muted-foreground">
                            {plan.creditsBullets.map((bullet) => (
                              <li key={bullet.key} className="flex gap-1.5">
                                <span className="text-muted-foreground">•</span>
                                <span>
                                  {t(bullet.key, {
                                    count: bullet.count,
                                    defaultValue: bullet.text,
                                  })}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <ul className="space-y-1.5 mb-2 flex-1">
                          {plan.features.map((feature) => (
                            <li
                              key={feature.nameKey}
                              className="flex items-start gap-2 text-xs"
                            >
                              {feature.included ? (
                                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                              ) : (
                                <X className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                              )}
                              <span
                                className={
                                  feature.included
                                    ? "text-foreground"
                                    : "text-muted-foreground"
                                }
                              >
                                {t(feature.nameKey, {
                                  defaultValue: feature.name,
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={handleViewAllPlans}
                  className="w-full text-center text-sm text-primary hover:text-primary transition-colors mt-6"
                >
                  {t("pricing.viewAllPlans")}
                </button>
              </>
            )}

            <PricingFAQSection
              compact
              className="mt-8 border-t border-border pt-7"
            />
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        redirectPath="/subscription"
      />

    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Top-up packs panel — rendered inside UpgradeModal when tab === "topup".
// Mirrors the marketing pricing card layout but uses authenticated checkout.
// ───────────────────────────────────────────────────────────────────────────
type TopUpPacksPanelProps = {
  packs:
    | Record<
        CreditPackId,
        {
          credits: number;
          price: number;
          discount?: string;
          label?: string;
        }
      >
    | undefined;
  onBuy: (packId: CreditPackId) => void;
  isLoading: boolean;
  hasActivePaidSub: boolean;
  isAuthenticated: boolean;
  onSwitchToPlans: () => void;
};

function TopUpPacksPanel({
  packs,
  onBuy,
  isLoading,
  hasActivePaidSub,
  isAuthenticated,
  onSwitchToPlans,
}: TopUpPacksPanelProps) {
  const { t } = useTranslation();
  const packIds = CREDIT_PACK_ORDER;
  return (
    <div className="space-y-4 sm:space-y-5">
      <p className="text-xs sm:text-sm text-muted-foreground">
        {t("pricing.topupOneTimePacks")}
      </p>

      {isAuthenticated && !hasActivePaidSub && (
        <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-xs sm:text-sm text-primary">
          {t("pricing.topupNoPlanBanner")}{" "}
          <button
            onClick={onSwitchToPlans}
            className="underline hover:text-primary"
          >
            {t("pricing.comparePlans")}
          </button>
          .
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {packIds.map((packId) => {
          const pack = packs?.[packId];
          const isBest = packId === BEST_VALUE_PACK_ID;
          if (!pack) {
            return (
              <div
                key={packId}
                className="rounded-xl border border-border bg-card p-4 h-32 animate-pulse"
              />
            );
          }
          return (
            <button
              key={packId}
              onClick={() => onBuy(packId)}
              disabled={isLoading}
              className={`flex flex-col items-center text-center rounded-xl border-2 p-4 transition-all ${
                isBest
                  ? "subscription-recommended bg-card"
                  : "border-border bg-card hover:border-border"
              } ${isLoading ? "opacity-60 cursor-wait" : ""}`}
            >
              {isBest && (
                <span className="text-[9px] font-bold subscription-badge px-2 py-0.5 rounded-full mb-1">
                  {t("pricing.bestValue")}
                </span>
              )}
              <p className="text-xl sm:text-2xl font-bold text-foreground">
                {pack.credits.toLocaleString()}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-2">
                {t("pricing.creditsLabelLower")}
              </p>
              <p className="text-base sm:text-lg font-semibold text-foreground">
                ${pack.price}
              </p>
              {pack.discount && (
                <p className="text-[10px] sm:text-xs text-primary mt-0.5">
                  {pack.discount}
                </p>
              )}
              <span
                className={`mt-auto pt-3 w-full ${isLoading ? "pointer-events-none" : ""}`}
              >
                <span
                  className={`block w-full rounded-lg py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                    isBest
                      ? "subscription-badge"
                      : "bg-white/10 text-foreground hover:bg-white/20"
                  }`}
                >
                  {t("subscription.buyNow")}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground text-center">
        {t("pricing.needRecurringPrefix")}{" "}
        <button
          onClick={onSwitchToPlans}
          className="underline hover:text-foreground"
        >
          {t("pricing.monthlyOrYearlySwitch")}
        </button>{" "}
        {t("pricing.needRecurringSuffix")}
      </p>
    </div>
  );
}
